import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext, useParams } from "react-router-dom";
import { useDisplayModalConfirm } from "../shared/useDisplayModalConfirm";
import { useAntTheme } from "hooks/useAntTheme/";
import { useAnimation } from "../../useAnimation";

function useNavigationBlocker(shouldBlock: number) {
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [lastLocation, setLastLocation] = useState<unknown>(null);
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { displayModalConfirm } = useDisplayModalConfirm();
  const params = useParams<{ id: string }>();
  const { theme } = useAntTheme();
  const { id: projectId } = params ?? {};
  const { projectData } = useAnimation(projectId);

  const confirmedNavigationRef = useRef(false);
  const lastLocationRef = useRef<unknown>(null);
  const originalPushRef = useRef<typeof navigator.push | null>(null);

  useEffect(() => {
    confirmedNavigationRef.current = confirmedNavigation;
  }, [confirmedNavigation]);

  useEffect(() => {
    lastLocationRef.current = lastLocation;
  }, [lastLocation]);

  const handleNavigationAttempt = useCallback(
    (args: Parameters<typeof navigator.push>) => {
      if (confirmedNavigationRef.current) {
        // ALLOW ORIGINAL PUSH TEMPORARILY TO ALLOW NAVIGATION
        if (originalPushRef.current) {
          navigator.push = originalPushRef.current;
          originalPushRef.current = null;
        }
        navigator.push(...args);
        setConfirmedNavigation(false);
        return;
      }
      displayModalConfirm(projectData?.data.id, theme.colorPrimary, () => {
        setConfirmedNavigation(true);
      });
      setLastLocation(args);
    },
    [navigator, displayModalConfirm, projectData?.data.id, theme.colorPrimary]
  );

  // SETUP UP BLOCKING WHEN THERE ARE CHANGES
  useEffect(() => {
    if (shouldBlock === 0) {
      if (originalPushRef.current) {
        navigator.push = originalPushRef.current;
        originalPushRef.current = null;
      }
      return;
    }
    if (!originalPushRef.current) {
      originalPushRef.current = navigator.push;
      navigator.push = (...args: Parameters<typeof navigator.push>) => {
        handleNavigationAttempt(args);
      };
    }

    return () => {
      if (originalPushRef.current) {
        navigator.push = originalPushRef.current;
        originalPushRef.current = null;
      }
    };
  }, [shouldBlock, navigator, handleNavigationAttempt]);

  // CONFIRM NAVIGATION
  useEffect(() => {
    if (confirmedNavigation && lastLocationRef.current) {
      navigator.push(
        ...(lastLocationRef.current as Parameters<typeof navigator.push>)
      );
      setLastLocation(null);
    }
  }, [confirmedNavigation, navigator]);

  const disableBlocking = useCallback(() => {
    if (originalPushRef.current) {
      navigator.push = originalPushRef.current;
      originalPushRef.current = null;
    }
  }, [navigator]);

  return {
    disableBlocking,
  };
}

export default useNavigationBlocker;
